<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>

		<main-content>
			<template #content>
				<div class="columns column6">
					<h1>{{ page.title || page.header }}</h1>
					<p v-if="page.bookingID">
						<book-button class="button cta" :data-roomID="page.booking - id">
							{{ $t('bookThisRoom') }}
						</book-button>
					</p>
				</div>
				<div class="columns column6">
					<div v-parse-links v-html="page.content" />
				</div>
			</template>
		</main-content>

		<section class="package-item-section">
			<div class="row">
				<div class="columns column5 align-left">
					<main-content
						:title="page.title || page.header"
						:content="page.content"
						:intro-image="{
							image: page.introductionImage,
							imageWebp: page.introductionImageWebp,
						}"
					>
						<template #extra-content>
							<div class="special-content align-left">
								<picture>
									<source :srcset="page.extraImageWebp" type="image/webp" />
									<source :srcset="page.extraImage" />
									<img
										:src="page.extraImage"
										:alt="page.extraImageAlt"
										class="extra-image"
										loading="lazy"
									/>
								</picture>

								<div v-parse-links v-html="page.extraContent" />

								<h3>{{ $t('facilities') }}</h3>
								<div class="room-icons">
									<div v-for="facility in page.facilities" :key="facility.id" class="icon-holder">
										<picture>
											<source :srcset="facility.iconWebp" type="image/webp" />
											<source :srcset="facility.icon" />
											<img
												:src="facility.icon"
												:alt="facility.iconAlt"
												class="facility-icon"
												loading="lazy"
											/>
										</picture>
										<span>{{ facility.header }}</span>
									</div>
								</div>
								<picture>
									<source :srcset="page.extraImage2Webp" type="image/webp" />
									<source :srcset="page.extraImage2" />
									<img
										:src="page.extraImage2"
										:alt="page.extraImage2Alt"
										class="extra-image"
										loading="lazy"
									/>
								</picture>
							</div>
						</template>
					</main-content>
				</div>
				<div class="columns column2 divider-column align-center">
					<div class="vertical-divider" />
				</div>
				<div class="columns column5 side-image">
					<div class="row">
						<picture>
							<source :srcset="page.sideImageWebp" type="image/webp" />
							<source :srcset="page.sideImage" />
							<img class="card-image" :src="page.sideImage" :alt="page.sideImageAlt" loading="lazy" />
						</picture>
					</div>
				</div>
			</div>
			<div class="row">
				<div class="columns column12 align-center button-row">
					<nuxt-link class="button no-fill" :to="localePath('rooms')">
						{{ $t('seeOtherRooms') }}
					</nuxt-link>
					<nuxt-link v-if="page.bookingUrl" class="button button-cta" target="_blank" :to="page.bookingUrl">
						<span>{{ $t('bookThisRoom') }}</span>
					</nuxt-link>
					<book-button v-else :roomid="page.bookingID" class="button button-cta">
						<span>{{ $t('bookThisRoom') }}</span>
					</book-button>
				</div>
			</div>
		</section>

		<section v-if="page.footerSection" class="footer-images">
			<div class="row carousel-row">
				<bc-carousel
					ref="carousel"
					:items-to-show="1.2"
					class="packages-slider"
					:wrap-around="true"
					:breakpoints="breakpoints"
				>
					<template #addons>
						<bc-navigation class="slider-navigation">
							<template #next>
								<div class="slider-button slider-next" />
							</template>
							<template #prev>
								<div class="slider-button slider-prev" />
							</template>
						</bc-navigation>
					</template>
					<bc-slide v-for="item in page.footerSection" :key="item.ID" class="package-item slider-item">
						<picture>
							<source :srcset="item.imageWebp" type="image/webp" />
							<source :srcset="item.image" />
							<img :src="item.image" :alt="item.imageAlt" loading="lazy" />
						</picture>
					</bc-slide>
				</bc-carousel>
			</div>
		</section>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const localePath = useLocalePath();

await fetchPage();

const breakpoints = {
	700: {
		itemsToShow: 2,
	},
	1080: {
		itemsToShow: 3,
	},
};
</script>

<style lang="scss" scoped>
/* stylelint-disable selector-class-pattern */
:deep(.slider-navigation) {
	position: absolute;
	bottom: 0;
	left: calc(50% - 75px);
	top: unset;
	transform: unset;
	width: 67px;
	height: 67px;
	margin: 0;
	padding: 0;

	&.carousel__next {
		left: unset;
		right: calc(50% - 75px);
	}
}

.carousel__slide {
	padding: 10px;
}
/* stylelint-enable selector-class-pattern */

.footer-images {
	padding: 25px 0 75px;
}

.carousel-row {
	max-width: 1600px;
	padding: 0 10px;
}

.carousel {
	width: 100%;
	padding: 0 0 100px;

	img {
		max-width: 500px;
	}
}

.package-item-section {
	padding: 75px 0 25px;

	.row {
		position: relative;
		flex-flow: row wrap;
		justify-content: space-between;
		align-items: flex-start;
	}
}

.main-content {
	text-align: left;
	padding: 0;

	:deep(.columns) {
		text-align: left;
		padding: 0;
	}

	:deep(ul) {
		padding: 0 0 0 20px;
	}

	:deep(li) {
		width: 50%;
		float: left;
		padding: 0 50px 0 0;
	}

	:deep(h1),
	:deep(h2),
	:deep(h3) {
		margin: 0 0 10px;
	}

	h3,
	:deep(h3) {
		font-size: var(--h4-font-size);
		color: var(--body-color);
		font-weight: 500;
	}
}

.card-image {
	max-width: 500px;
	margin: 0 0 51px;
}

.side-image {
	position: sticky;
	left: 0;
	top: 100px;
}

.extra-image {
	margin: 0 0 40px;

	&:last-child {
		margin: 0;
	}
}

.special-content {
	padding: 40px 0 51px;

	h3,
	h4 {
		color: var(--body-color);
		font-weight: 500;
		margin: 0 0 10px;
	}

	.available-at {
		display: grid;
		grid-template-columns: 1fr 1fr;
		align-content: center;

		div {
			margin: 5px 0;
			display: flex;
			flex-flow: row nowrap;
			justify-content: flex-start;
			align-items: center;

			span {
				font-size: var(--button-font-size);
				margin: 0;
				color: #000;
			}

			img {
				margin: 0 12px 0 0;
				width: 40px;
				height: 40px;
			}
		}
	}
}

.button-row {
	margin: 25px auto 0;

	.button {
		margin-right: 15px;
	}
}

.vertical-divider {
	width: 1px;
	height: calc(100% - 51px);
	background: #3c3834;
	position: absolute;
	inset: 0 0 60px;
	margin: 0 auto;
}

.room-icons {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-start;
	align-items: flex-start;
	margin: 0 0 40px;
}

.icon-holder {
	color: var(--button-nofill-color);
	width: 49%;
	margin: 10px 1% 10px 0;
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: center;

	img {
		margin: 0 15px 0 0;
		max-width: 25px;
	}
}

.room-item .room-icons {
	max-width: 600px;
}

@media (max-width: 1080px) {
	.divider-column {
		display: none;
	}

	.card-image {
		margin: 25px auto 50px;
		position: relative;
		left: auto;
		top: auto;
	}

	.column5 {
		width: 100%;
	}

	.side-image {
		position: relative;
		left: auto;
		top: auto;

		.row {
			max-width: 500px;
		}
	}

	.carousel-row {
		padding: 0;
	}
}

@media (max-width: 680px) {
	.icon-holder {
		width: 99%;
	}
}
</style>
